// Public, read-only proposal view served at /p/{tenant_slug}/{token}.
// Called by clients (no login). Hits /api/public/proposals/... which itself
// handles access control + hide_prices redaction.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Lock, Mail, Loader2, FileText } from "lucide-react";
import axios from "axios";
import { VideoBlockRender } from "../components/VideoBlock";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Curated gradient banners — same list as the builder so preview and public
// view stay in perfect sync when nothing has been uploaded.
const HEADER_GRADIENTS = [
  { id: "iris",     style: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
  { id: "seafoam",  style: "linear-gradient(135deg, #43cea2 0%, #185a9d 100%)" },
  { id: "sunset",   style: "linear-gradient(135deg, #ff9966 0%, #ff5e62 100%)" },
  { id: "aurora",   style: "linear-gradient(135deg, #7f7fd5 0%, #86a8e7 50%, #91eae4 100%)" },
  { id: "pragma",   style: "linear-gradient(135deg, #5B4FE8 0%, #8b7cff 100%)" },
  { id: "ember",    style: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)" },
  { id: "midnight", style: "linear-gradient(135deg, #0f172a 0%, #334155 100%)" },
  { id: "coral",    style: "linear-gradient(135deg, #ff6a88 0%, #ffb199 100%)" },
];
function pickGradient(id) {
  let sum = 0;
  for (let i = 0; i < (id || "").length; i++) sum = (sum + id.charCodeAt(i)) | 0;
  return HEADER_GRADIENTS[Math.abs(sum) % HEADER_GRADIENTS.length];
}
function resolveHeader(url, id) {
  if (!url) return { kind: "gradient", style: pickGradient(id).style };
  if (url.startsWith("gradient:")) {
    const g = HEADER_GRADIENTS.find((g) => g.id === url.slice("gradient:".length));
    return { kind: "gradient", style: (g || pickGradient(id)).style };
  }
  return { kind: "image", url };
}

const fmtMoney = (currency, n) => {
  if (n == null) return "—";
  const v = Number.isFinite(+n) ? +n : 0;
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(v); }
  catch { return `${currency || "USD"} ${v.toFixed(2)}`; }
};

const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
};

export default function PublicProposal() {
  const params = useParams();
  // The route matches either /p/:token or /p/:tenant_slug/:token.
  const token = params.token;
  const tenant_slug = params.tenant_slug || "";
  const [state, setState] = useState({ loading: true, data: null, needsEmail: false, error: null });
  const [emailInput, setEmailInput] = useState("");
  const [decision, setDecision] = useState({ working: false, done: null });
  const [showAccept, setShowAccept] = useState(false);
  const [sigName, setSigName] = useState("");

  const load = async (email) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const base = tenant_slug
        ? `${API}/public/proposals/${tenant_slug}/${token}`
        : `${API}/public/proposals/${token}`;
      const url = `${base}${email ? `?email=${encodeURIComponent(email)}` : ""}`;
      const { data } = await axios.get(url);
      if (data.needs_email) {
        setState({ loading: false, data: null, needsEmail: true, error: null, tenantName: data.tenant_name });
        return;
      }
      setState({ loading: false, data, needsEmail: false, error: null });
    } catch (e) {
      setState({ loading: false, data: null, needsEmail: false, error: e.response?.status === 404 ? "not_found" : "error" });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenant_slug, token]);

  const respond = async (kind) => {
    // Reject flows straight through. Accept opens a signature dialog first.
    if (kind === "accept") { setShowAccept(true); return; }
    setDecision({ working: true, done: null });
    try {
      const base = tenant_slug
        ? `${API}/public/proposals/${tenant_slug}/${token}/decision`
        : `${API}/public/proposals/${token}/decision`;
      await axios.post(base, {
        decision: "reject",
        email: emailInput || undefined,
      });
      setDecision({ working: false, done: "reject" });
    } catch (e) {
      alert(e.response?.data?.detail || "Something went wrong");
      setDecision({ working: false, done: null });
    }
  };

  const confirmAccept = async () => {
    const nm = sigName.trim();
    if (!nm) return;
    setDecision({ working: true, done: null });
    try {
      const base = tenant_slug
        ? `${API}/public/proposals/${tenant_slug}/${token}/decision`
        : `${API}/public/proposals/${token}/decision`;
      await axios.post(base, {
        decision: "accept",
        email: emailInput || undefined,
        signature_name: nm,
      });
      setDecision({ working: false, done: "accept" });
      setShowAccept(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Something went wrong");
      setDecision({ working: false, done: null });
    }
  };

  if (state.loading) {
    return <FullscreenCenter><Loader2 size={22} className="animate-spin" /><span className="ml-2">Loading…</span></FullscreenCenter>;
  }

  if (state.error === "not_found") {
    return <FullscreenCenter>
      <div className="text-center">
        <FileText size={40} style={{ color: "#94a3b8", margin: "0 auto 12px" }} />
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 6px" }}>Proposal not available</h1>
        <p style={{ color: "#64748b" }}>This link may have expired or the proposal has been withdrawn.</p>
      </div>
    </FullscreenCenter>;
  }

  if (state.needsEmail) {
    return (
      <FullscreenCenter>
        <div style={{ maxWidth: 400 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: "rgba(91,79,232,0.10)",
            color: "#5B4FE8", display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 16px",
          }}>
            <Lock size={22} />
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 600, textAlign: "center", margin: "0 0 4px" }}>Verify your email</h1>
          <p style={{ color: "#64748b", textAlign: "center", marginBottom: 20, fontSize: 14 }}>
            {state.tenantName || "This workspace"} restricted access to this proposal. Enter the email address it was sent to.
          </p>
          <input
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="you@company.com"
            className="pg-input w-full"
            style={{ marginBottom: 12 }}
            data-testid="public-verify-email"
          />
          <button
            className="pg-btn pg-btn-primary w-full"
            onClick={() => load(emailInput)}
            data-testid="public-verify-submit"
          >
            <Mail size={14} /> Continue
          </button>
        </div>
      </FullscreenCenter>
    );
  }

  const d = state.data;
  if (!d) return null;
  const { proposal, blocks, totals, branding } = d;
  const currency = proposal.currency || "USD";
  const canRespond = proposal.allow_response && proposal.status === "published";

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "40px 20px" }} data-testid="public-proposal">
      <div style={{
        maxWidth: 820, margin: "0 auto", background: "#ffffff",
        borderRadius: 12, boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}>
        {/* Header banner */}
        {(() => {
          const hdr = resolveHeader(proposal.header_image_url, proposal.id);
          return (
            <div style={{ height: 180, background: hdr.kind === "gradient" ? hdr.style : undefined, overflow: "hidden" }}>
              {hdr.kind === "image" && <img src={hdr.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
            </div>
          );
        })()}

        {/* Branded header */}
        <div style={{ padding: "24px 32px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {branding.logo_url && <img src={branding.logo_url} alt="" style={{ height: 32 }} />}
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{branding.company_name}</div>
              {branding.address && <div style={{ fontSize: 11, color: "#64748b" }}>{branding.address}</div>}
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: 11, color: "#64748b" }}>
            <div>{proposal.number}</div>
            <div>{fmtDate(proposal.display_date)}</div>
          </div>
        </div>

        <div style={{ padding: "32px 32px 40px" }}>
          <h1 style={{ fontSize: 30, fontWeight: 700, margin: "0 0 20px", letterSpacing: "-0.01em" }}>{proposal.name}</h1>

          {proposal.status === "accepted" && (
            <div style={{ marginBottom: 8 }}>
              <StatusBanner tone="accepted" text="This proposal has been accepted." />
              {proposal.signature_name && (
                <div style={{ marginTop: 12, padding: 14, border: "1px solid rgba(22,163,74,0.4)", borderRadius: 8, background: "rgba(22,163,74,0.05)" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Accepted by</div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{proposal.signature_name}</div>
                  {proposal.signature_date && (
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                      {new Date(proposal.signature_date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {proposal.status === "rejected" && (
            <StatusBanner tone="rejected" text="This proposal was declined." />
          )}

          <div style={{ marginTop: 24 }}>
            {blocks.map((b) => <PublicBlock key={b.id} block={b} currency={currency} hidePrices={proposal.hide_prices} />)}
          </div>

          {/* Totals */}
          {totals && (totals.grand_total != null) && (
            <div style={{ marginTop: 40, paddingTop: 24, borderTop: "2px solid #0f172a" }}>
              <table className="w-full" style={{ fontSize: 14 }}>
                <tbody>
                  {(totals.by_unit || []).map((row) => (
                    <tr key={row.unit}>
                      <td style={{ padding: "4px 0", color: "#64748b" }}>{row.unit} services</td>
                      <td style={{ padding: "4px 0", textAlign: "right" }}>{fmtMoney(currency, row.amount)}</td>
                    </tr>
                  ))}
                  {totals.subtotal_discount_amount > 0 && (
                    <tr>
                      <td style={{ padding: "4px 0", color: "#64748b" }}>Discount</td>
                      <td style={{ padding: "4px 0", textAlign: "right", color: "#dc2626" }}>−{fmtMoney(currency, totals.subtotal_discount_amount)}</td>
                    </tr>
                  )}
                  {totals.subtotal != null && (
                    <tr style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "6px 0", color: "#64748b" }}>Subtotal</td>
                      <td style={{ padding: "6px 0", textAlign: "right" }}>{fmtMoney(currency, totals.subtotal)}</td>
                    </tr>
                  )}
                  {totals.vat_amount != null && totals.vat_percent > 0 && (
                    <tr>
                      <td style={{ padding: "4px 0", color: "#64748b" }}>Tax ({totals.vat_percent}%)</td>
                      <td style={{ padding: "4px 0", textAlign: "right" }}>{fmtMoney(currency, totals.vat_amount)}</td>
                    </tr>
                  )}
                  <tr style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "10px 0", fontWeight: 600, fontSize: 15 }}>Total</td>
                    <td style={{ padding: "10px 0", textAlign: "right", fontWeight: 600, fontSize: 22 }}>{fmtMoney(currency, totals.grand_total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {proposal.additional_information && (
            <div style={{ marginTop: 24, padding: 16, background: "#f1f5f9", borderRadius: 8, fontSize: 13, color: "#475569", whiteSpace: "pre-wrap" }}>
              {proposal.additional_information}
            </div>
          )}

          {canRespond && decision.done == null && (
            <div style={{ marginTop: 40, display: "flex", justifyContent: "center", gap: 12 }}>
              <button
                onClick={() => respond("reject")}
                disabled={decision.working}
                data-testid="public-reject-btn"
                style={{
                  padding: "12px 24px", borderRadius: 8, fontSize: 14, fontWeight: 500,
                  background: "#ffffff", color: "#64748b", border: "1px solid #e5e7eb", cursor: "pointer",
                }}
              >
                Decline
              </button>
              <button
                onClick={() => respond("accept")}
                disabled={decision.working}
                data-testid="public-accept-btn"
                style={{
                  padding: "12px 32px", borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: "#16a34a", color: "#ffffff", border: "none", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 8,
                }}
              >
                <CheckCircle2 size={16} /> Accept proposal
              </button>
            </div>
          )}

          {decision.done && (
            <div style={{ marginTop: 40, textAlign: "center" }}>
              <StatusBanner tone={decision.done === "accept" ? "accepted" : "rejected"}
                text={decision.done === "accept" ? "Thanks — the proposal has been accepted." : "You've declined this proposal."} />
            </div>
          )}
        </div>
      </div>
      <div style={{ maxWidth: 820, margin: "20px auto 0", textAlign: "center", fontSize: 11, color: "#94a3b8" }}>
        Powered by Pragma
      </div>

      {showAccept && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: 20 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowAccept(false); }}
          data-testid="public-accept-modal"
        >
          <div style={{
            background: "#fff", borderRadius: 14, padding: 28, width: 460, maxWidth: "94vw",
            boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, background: "rgba(22,163,74,0.12)",
              color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
            }}>
              <CheckCircle2 size={22} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 600, textAlign: "center", margin: "0 0 4px", color: "#0f172a" }}>Accept this proposal</h2>
            <p style={{ fontSize: 13, color: "#64748b", textAlign: "center", margin: "0 0 20px" }}>
              Type your full name to confirm you accept the terms and pricing. This creates a signed record of your acceptance.
            </p>
            <label style={{ fontSize: 11, color: "#64748b", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, display: "block" }}>Full name</label>
            <input
              value={sigName}
              onChange={(e) => setSigName(e.target.value)}
              placeholder="Jane Doe"
              autoFocus
              className="pg-input"
              style={{ width: "100%", padding: "10px 12px", fontSize: 15, border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 4 }}
              data-testid="public-signature-name"
            />
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 20 }}>
              Signed on {new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowAccept(false)}
                style={{ padding: "10px 18px", background: "#fff", color: "#64748b", border: "1px solid #e5e7eb", borderRadius: 8, cursor: "pointer" }}
                data-testid="public-accept-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmAccept}
                disabled={!sigName.trim() || decision.working}
                style={{
                  padding: "10px 24px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer",
                  fontWeight: 600, opacity: (!sigName.trim() || decision.working) ? 0.6 : 1,
                }}
                data-testid="public-accept-confirm"
              >
                {decision.working ? "Submitting…" : "Confirm & Accept"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PublicBlock({ block, currency, hidePrices }) {
  const c = block.content || {};
  if (block.type === "heading") {
    return (
      <div style={{ marginTop: 28 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, margin: "0 0 4px" }}>{c.heading}</h2>
        {c.subheading && <div style={{ fontSize: 13, color: "#64748b" }}>{c.subheading}</div>}
      </div>
    );
  }
  if (block.type === "rich_text") {
    return <div style={{ marginTop: 14, fontSize: 14, lineHeight: 1.65, color: "#334155" }} dangerouslySetInnerHTML={{ __html: c.html || "" }} />;
  }
  if (block.type === "service") {
    const qty = Number(c.quantity || 0);
    const price = Number(c.unit_price || 0);
    const disc = Number(c.discount_pct || 0);
    const net = qty * price * (1 - disc / 100);
    return (
      <div style={{ marginTop: 14, padding: "14px 0", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 20 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{c.name_snapshot || "Service"}</div>
            {c.description_snapshot && (
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 4, whiteSpace: "pre-wrap" }}>{c.description_snapshot}</div>
            )}
            {!hidePrices && qty > 0 && (
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                {qty} × {fmtMoney(currency, price)}{disc > 0 && ` less ${disc}%`}
              </div>
            )}
          </div>
          {!hidePrices && (
            <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap" }}>{fmtMoney(currency, net)}</div>
          )}
        </div>
      </div>
    );
  }
  if (block.type === "cover_image" && c.url) {
    return <img src={c.url} alt="" style={{ width: "100%", borderRadius: 8, marginTop: 14 }} />;
  }
  if (block.type === "media" && c.url) {
    return <img src={c.url} alt="" style={{ maxWidth: "100%", borderRadius: 8, marginTop: 14 }} />;
  }
  if (block.type === "file" && c.url) {
    return (
      <a href={c.url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 14, padding: "8px 14px", background: "#f1f5f9", borderRadius: 8, fontSize: 13, color: "#5B4FE8", textDecoration: "none" }}>
        <FileText size={14} /> {c.label || "Attachment"}
      </a>
    );
  }
  if (block.type === "video") {
    return <div style={{ marginTop: 20 }}><VideoBlockRender video={c} /></div>;
  }
  return null;
}

function StatusBanner({ tone, text }) {
  const map = {
    accepted: { bg: "rgba(22,163,74,0.12)", fg: "#16a34a", icon: CheckCircle2 },
    rejected: { bg: "rgba(220,38,38,0.12)", fg: "#dc2626", icon: XCircle },
  };
  const m = map[tone] || map.accepted;
  const Icon = m.icon;
  return (
    <div style={{ background: m.bg, color: m.fg, padding: "10px 14px", borderRadius: 8, display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 500 }}>
      <Icon size={14} /> {text}
    </div>
  );
}

function FullscreenCenter({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      {children}
    </div>
  );
}
